import axiosConfig from "./axiosConfig";
import axios from "axios";

/**
 * Total upload attempts. Each attempt fetches a *fresh* presigned POST
 * from the backend before posting to S3, so:
 *   - expired/stale policies don't kill subsequent retries
 *   - the backend can issue a different key/policy if needed
 *
 * Backend caps presigned URLs at 30 per journey, so 3 retries is well
 * within the budget while comfortably covering transient network
 * blips. The persist provider reuses the same budget for symmetry.
 */
const MAX_UPLOAD_ATTEMPTS = 3;
const RETRY_BACKOFF_MS = [0, 500, 1500];
const UPLOAD_TIMEOUT_MS = 30000;

/**
 * Storage backend selector — temporary domain-based patch.
 *
 * Some regions ship without an S3 bucket provisioned and must upload
 * via Signzy Persist instead. Until the backend gains a per-journey
 * `storage` field in `/otk-service/get-upload-url`, the frontend picks
 * the provider from `window.location.hostname`:
 *
 *   kyc.signzy.ae                          → persist
 *   everything else (incl. localhost/dev)  → s3
 *
 * Hostnames matched case-insensitively; unknown / SSR / test contexts
 * fall back to s3 so a missing `window` never silently breaks uploads.
 * Callers see the same `{ s3Key, error }` shape either way — `s3Key`
 * is an opaque "stored-somewhere" marker, not an S3-specific claim.
 *
 * TODO: replace with a backend-driven `storage` field on the upload
 * spec (see CHANGELOG). This block goes away once that ships.
 */
const PROVIDER_S3 = "s3";
const PROVIDER_PERSIST = "persist";

const PERSIST_HOSTNAMES = new Set([
    "kyc.signzy.ae"
]);

function resolveProvider() {
    const host = (typeof window !== "undefined" && window.location?.hostname || "")
        .toString()
        .trim()
        .toLowerCase();
    return PERSIST_HOSTNAMES.has(host) ? PROVIDER_PERSIST : PROVIDER_S3;
}

/**
 * Region-scoped kill switch that predates the provider selector. Some
 * deployments have NEITHER S3 nor Persist reachable (fully offline
 * regions), so both providers must be bypassed entirely. Set
 *
 *   REACT_APP_STORAGE_ENABLED=false
 *
 * to short-circuit `convertImageToUrl` — it then returns
 * `{ s3Key: "", error: null }` without touching the network, and callers
 * fall through to the raw-base64 path they already use for terminal
 * upload failures (`{ frontDocBase64: image }` etc.).
 *
 * Default is ENABLED for backward compat — only the literal string "false"
 * (case-insensitive) or "0" disables the flow.
 */
function isStorageEnabled() {
    const raw = (process.env.REACT_APP_STORAGE_ENABLED ?? "").toString().trim().toLowerCase();
    return raw !== "false" && raw !== "0";
}

/**
 * Error categories surfaced to callers so the UI can decide between
 * "show retry button" vs. "fail soft, fall back to base64 payload" vs.
 * "show terminal error and bail to journey-stopped".
 */
export const UploadErrorCode = {
    INVALID_INPUT: "INVALID_INPUT",         // not a base64 data URI / bad mime
    MISSING_CONTEXT: "MISSING_CONTEXT",     // journeyId / fileType missing
    PRESIGN_FAILED: "PRESIGN_FAILED",       // backend rejected presigned-URL request
    // Legacy name kept for analytics/monitoring parity across the S3 and
    // Persist paths: "upstream storage rejected the upload".
    S3_UPLOAD_FAILED: "S3_UPLOAD_FAILED",
    NETWORK: "NETWORK",                     // navigator offline / timeout
    JOURNEY_INVALID: "JOURNEY_INVALID",     // backend says journey is done / expired
    RATE_LIMITED: "RATE_LIMITED",           // backend returned 429
    UNKNOWN: "UNKNOWN"
};

/**
 * Upload a captured image / PDF using the configured storage provider.
 *
 * Provider dispatch (hostname-driven, resolved per-call so tests can
 * flip `window.location.hostname` between assertions):
 *   - `s3`      → POST /otk-service/get-upload-url → presigned-POST → S3.
 *   - `persist` → POST /api/files/upload (nginx → persist.signzy.tech),
 *                 returning the persist `directURL` as the opaque `s3Key`.
 *
 * Flow per attempt (S3 path):
 *   1. POST /otk-service/get-upload-url → { presignedPost, s3Key }
 *   2. Build FormData with the policy fields + the binary blob
 *   3. POST directly to S3
 *   4. On success, return { s3Key, error: null }
 *   5. On failure, classify the error and either retry (transient) or
 *      bail (terminal — bad journey state, validation, etc.).
 *
 * Flow per attempt (persist path):
 *   1. POST /api/files/upload (FormData: file + ttl + mimetype)
 *   2. Persist returns `{ file: { directURL } }`; use directURL as s3Key.
 *   3. Retry on network / 5xx; bail on 4xx (bad payload — a fresh attempt
 *      won't help).
 *
 * Callers receive a structured result `{ s3Key, error }`:
 *   - `s3Key` populated → success; pass it to update-journey.
 *   - `error` populated → upload failed after all retries;
 *     UI surfaces a typed error and offers a retry / fallback path.
 *
 * The function ALSO returns `{ s3Key: "", error: null }` when
 * `REACT_APP_STORAGE_ENABLED=false` — the region-scoped kill switch —
 * and `{ s3Key: "", error }` on terminal upload failure, so existing
 * callers that fall back to sending raw base64
 * (`{ frontDocBase64: image }`) keep working — the `error` object is
 * opt-in.
 *
 * @param {string} base64Image - data: URI (`image/*` or `application/pdf`).
 * @param {string} journeyId
 * @param {string} fileType
 * @returns {Promise<{ s3Key: string, error: null | { code: string, message: string, status?: number, retriable: boolean } }>}
 */
export const convertImageToUrl = async (base64Image, journeyId, fileType) => {
    const validationError = validateInputs(base64Image, journeyId, fileType);
    if (validationError) {
        console.error("[base64ToUrlUtility] Input validation failed", validationError);
        return { s3Key: "", error: validationError };
    }

    // Region kill switch: no storage backend available in this deployment.
    // Skip provider dispatch entirely; callers handle the empty s3Key by
    // attaching the raw base64 to update-journey.
    if (!isStorageEnabled()) {
        console.log("[base64ToUrlUtility] REACT_APP_STORAGE_ENABLED=false — bypassing upload, callers fall back to base64", { journeyId, fileType });
        return { s3Key: "", error: null };
    }

    let blob, mimeType;
    try {
        ({ blob, mimeType } = base64ToBlob(base64Image));
    } catch (err) {
        return {
            s3Key: "",
            error: {
                code: UploadErrorCode.INVALID_INPUT,
                message: err?.message || "Failed to decode image",
                retriable: false
            }
        };
    }

    return uploadBlobWithRetries({ blob, mimeType, journeyId, fileType });
};

/**
 * Upload a File/Blob directly to S3 via a presigned POST — no base64 round-trip.
 *
 * Used by the Supporting Document step, where the applicant already holds a File
 * (a picked PDF/image), so reading it into base64 only to decode it back to a Blob
 * is pure waste. Shares the same presign + retry + error contract as convertImageToUrl.
 *
 * @param {File|Blob} file - the picked file (image/* or application/pdf).
 * @param {string} journeyId
 * @param {string} fileType
 * @returns {Promise<{ s3Key: string, error: null | { code: string, message: string, status?: number, retriable: boolean } }>}
 */
export const uploadFileToS3 = async (file, journeyId, fileType) => {
    if (!(file instanceof Blob)) {
        return {
            s3Key: "",
            error: {
                code: UploadErrorCode.INVALID_INPUT,
                message: "No file provided to upload.",
                retriable: false
            }
        };
    }
    if (!journeyId || !fileType) {
        return {
            s3Key: "",
            error: {
                code: UploadErrorCode.MISSING_CONTEXT,
                message: "Missing journeyId or fileType for upload.",
                retriable: false
            }
        };
    }
    // The browser may report `application/x-pdf`; S3 policy accepts `application/pdf` only.
    const detected = file.type || "application/octet-stream";
    const mimeType = detected === "application/x-pdf" ? "application/pdf" : detected;

    return uploadBlobWithRetries({ blob: file, mimeType, journeyId, fileType });
};

/**
 * Shared upload loop for both entry points: fetch a fresh presigned POST and post the blob
 * to S3, retrying transient failures with a new URL each attempt.
 */
async function uploadBlobWithRetries({ blob, mimeType, journeyId, fileType }) {
    const provider = resolveProvider();
    console.log("[base64ToUrlUtility] Starting upload", {
        provider,
        journeyId,
        fileType,
        mimeType,
        sizeBytes: blob.size
    });

    let lastError = null;
    for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt++) {
        if (RETRY_BACKOFF_MS[attempt - 1] > 0) {
            await sleep(RETRY_BACKOFF_MS[attempt - 1]);
        }

        if (typeof navigator !== "undefined" && navigator.onLine === false) {
            lastError = {
                code: UploadErrorCode.NETWORK,
                message: "You appear to be offline. Check your connection and retry.",
                retriable: true
            };
            console.warn(`[base64ToUrlUtility] Attempt ${attempt}/${MAX_UPLOAD_ATTEMPTS}: offline`);
            continue;
        }

        const attemptResult = provider === PROVIDER_PERSIST
            ? await uploadViaPersist({ journeyId, fileType, blob, mimeType })
            : await uploadViaS3({ journeyId, fileType, blob, mimeType });

        if (attemptResult.error) {
            lastError = attemptResult.error;
            console.error(`[base64ToUrlUtility] Attempt ${attempt}/${MAX_UPLOAD_ATTEMPTS}: ${provider} upload failed`, attemptResult.error);
            if (!attemptResult.error.retriable) break;
            continue;
        }

        console.log(`[base64ToUrlUtility] Upload succeeded on attempt ${attempt}`, { provider, s3Key: attemptResult.s3Key });
        return { s3Key: attemptResult.s3Key, error: null };
    }

    return { s3Key: "", error: lastError || {
        code: UploadErrorCode.UNKNOWN,
        message: "Upload failed for an unknown reason",
        retriable: true
    } };
}

/**
 * One S3-provider upload attempt: fetch a fresh presigned POST from the
 * backend and push the blob to S3. Returns `{ s3Key }` on success or
 * `{ error }` on failure. Kept separate from `uploadViaPersist` so the
 * outer retry loop can dispatch by provider without branching.
 */
async function uploadViaS3({ journeyId, fileType, blob, mimeType }) {
    const presignResult = await fetchPresignedPost({ journeyId, fileType, mimeType });
    if (presignResult.error) return { error: presignResult.error };

    const uploadResult = await postBlobToS3({
        presignedPost: presignResult.presignedPost,
        blob,
        mimeType
    });
    if (uploadResult.error) return { error: uploadResult.error };

    return { s3Key: presignResult.s3Key };
}

function validateInputs(base64Image, journeyId, fileType) {
    if (
        typeof base64Image !== "string" ||
        (!base64Image.startsWith("data:image/") &&
            !base64Image.startsWith("data:application/pdf") &&
            !base64Image.startsWith("data:application/x-pdf"))
    ) {
        return {
            code: UploadErrorCode.INVALID_INPUT,
            message: "Captured payload is not a valid image or PDF.",
            retriable: false
        };
    }
    if (!journeyId || !fileType) {
        return {
            code: UploadErrorCode.MISSING_CONTEXT,
            message: "Missing journeyId or fileType for upload.",
            retriable: false
        };
    }
    return null;
}

function base64ToBlob(base64Image) {
    const detectedMime = base64Image.match(/data:([^;]+)/)?.[1] || "image/jpeg";
    // The browser may send `application/x-pdf` for some PDFs — the
    // backend / S3 policy accepts `application/pdf` only, so coerce.
    const mimeType = detectedMime === "application/x-pdf" ? "application/pdf" : detectedMime;

    const base64Data = base64Image.split(",")[1];
    if (!base64Data) {
        throw new Error("Empty base64 payload after the comma separator");
    }
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
    return { blob, mimeType };
}

async function fetchPresignedPost({ journeyId, fileType, mimeType }) {
    try {
        const response = await axiosConfig.post(
            "/otk-service/get-upload-url",
            { journeyId, fileType, mimeType },
            { timeout: 15000 }
        );
        const result = response?.data?.result;
        if (!result?.presignedPost?.url || !result?.s3Key) {
            return {
                error: {
                    code: UploadErrorCode.PRESIGN_FAILED,
                    message: "Backend returned a malformed presigned-POST response.",
                    retriable: true
                }
            };
        }
        return { presignedPost: result.presignedPost, s3Key: result.s3Key };
    } catch (err) {
        return { error: classifyAxiosError(err) };
    }
}

async function postBlobToS3({ presignedPost, blob, mimeType }) {
    const { url, fields } = presignedPost;
    const formData = new FormData();
    // Order matters for presigned POST — policy fields must appear
    // before the file part.
    Object.entries(fields || {}).forEach(([key, value]) => {
        formData.append(key, value);
    });
    const filename = mimeType === "application/pdf" ? "document.pdf" : "image.bin";
    formData.append("file", blob, filename);

    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
        const s3Response = await fetch(url, {
            method: "POST",
            body: formData,
            signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (s3Response.ok) return { ok: true };

        const text = await s3Response.text().catch(() => "");
        // 4xx from S3 is a request/policy rejection (empty or malformed
        // body, file too large, wrong content type, bad signature) — the
        // same payload fails the same way on every attempt, and each
        // retry burns a presign from the 30-per-journey budget. Callers
        // degrade to the base64 fallback instead. Only 5xx (S3-side
        // transient) retries.
        const status = s3Response.status;
        const retriable = status >= 500;
        return {
            error: {
                code: UploadErrorCode.S3_UPLOAD_FAILED,
                status,
                message: `S3 upload returned ${status}${text ? `: ${text.slice(0, 160)}` : ""}`,
                retriable
            }
        };
    } catch (err) {
        if (err?.name === "AbortError") {
            return {
                error: {
                    code: UploadErrorCode.NETWORK,
                    message: `Upload timed out after ${UPLOAD_TIMEOUT_MS / 1000} seconds.`,
                    retriable: true
                }
            };
        }
        return {
            error: {
                code: UploadErrorCode.NETWORK,
                message: err?.message || "Network failure during S3 upload.",
                retriable: true
            }
        };
    }
}

/**
 * One persist-provider upload attempt: push the blob directly to the
 * Signzy Persist file-upload endpoint.
 *
 * `/api/*` is proxied to `persist.signzy.tech/api/` by nginx (see
 * `conf/one-journey.local` and the production nginx config), so the
 * frontend never talks to Persist cross-origin. Response shape mirrors
 * the historical flow: `{ file: { directURL } }` on success.
 *
 * No client-side timeout is applied — slow connections with large
 * captures are allowed to finish rather than being aborted.
 *
 * TTL is fixed to "3 years" for KYC artefacts: downstream verification,
 * dashboard playback, and compliance audits all dereference the URL long
 * after the journey completes, so a short-lived link would strand the
 * document. Three years covers the retention window without keeping PII
 * around indefinitely.
 */
async function uploadViaPersist({ journeyId, fileType, blob, mimeType }) {
    const formData = new FormData();
    const filename = mimeType === "application/pdf" ? "document.pdf" : "image.png";
    formData.append("file", blob, filename);
    formData.append("ttl", "3 years");
    formData.append("mimetype", mimeType);

    try {
        // Use bare `axios` (not `axiosConfig`) so `baseURL` doesn't get
        // prepended — nginx routes `/api/` locally. FormData sets its own
        // multipart Content-Type + boundary; do not override.
        const response = await axios.post("/api/files/upload", formData);
        const directURL = response?.data?.file?.directURL;
        if (!directURL) {
            return {
                error: {
                    code: UploadErrorCode.S3_UPLOAD_FAILED,
                    message: "Persist returned success without a directURL.",
                    // Retriable in case Persist blipped a partial response;
                    // caps out at MAX_UPLOAD_ATTEMPTS regardless.
                    retriable: true
                }
            };
        }
        // Log the journey / fileType alongside the returned URL so persist
        // uploads are traceable in browser logs the same way S3 keys are.
        console.log("[base64ToUrlUtility] persist upload accepted", { journeyId, fileType });
        return { s3Key: directURL };
    } catch (err) {
        return { error: classifyPersistError(err) };
    }
}

/**
 * Persist-side error classifier. Persist is a plain HTTP file store — no
 * journey/state-machine semantics — so we only care about the axios
 * transport error shape:
 *   - network / timeout → retriable NETWORK
 *   - 4xx (bad payload, oversized, unsupported mime) → non-retriable
 *     S3_UPLOAD_FAILED (a fresh attempt won't help)
 *   - 5xx → retriable S3_UPLOAD_FAILED (persist is transient / restarting)
 *
 * Uses S3_UPLOAD_FAILED so downstream monitoring dashboards treat both
 * providers as "storage upload failure" without a new bucket.
 */
function classifyPersistError(err) {
    if (err?.code === "ECONNABORTED" || err?.code === "ERR_NETWORK") {
        return {
            code: UploadErrorCode.NETWORK,
            message: `Network connection to persist failed${err?.code ? ` (${err.code})` : ""}.`,
            retriable: true
        };
    }
    const status = err?.response?.status;
    const persistMessage = err?.response?.data?.error?.message
        || err?.response?.data?.message
        || err?.message;
    if (typeof status === "number") {
        const retriable = status >= 500;
        return {
            code: UploadErrorCode.S3_UPLOAD_FAILED,
            status,
            message: `Persist upload returned ${status}${persistMessage ? `: ${persistMessage.toString().slice(0, 160)}` : ""}`,
            retriable
        };
    }
    return {
        code: UploadErrorCode.NETWORK,
        message: persistMessage || "Unknown network failure contacting persist.",
        retriable: true
    };
}

function classifyAxiosError(err) {
    const status = err?.response?.status;
    const backendCode = err?.response?.data?.error?.type
        || err?.response?.data?.error?.status
        || err?.response?.data?.error?.message;
    const backendMessage = err?.response?.data?.error?.message
        || err?.message
        || "Failed to contact the backend.";

    if (err?.code === "ECONNABORTED" || err?.code === "ERR_NETWORK") {
        return {
            code: UploadErrorCode.NETWORK,
            message: "Network connection to the server failed.",
            retriable: true
        };
    }
    if (status === 429) {
        // Backend has terminated the journey at this point — set the
        // terminateJourney flag so capture screens can hand control
        // back to Settings.jsx, which surfaces VerificationTerminated.
        return {
            code: UploadErrorCode.RATE_LIMITED,
            message: backendMessage || "Upload limit exceeded for this journey. This session will end.",
            status,
            retriable: false,
            terminateJourney: true
        };
    }
    if (status === 409 || status === 410 || status === 422) {
        return {
            code: UploadErrorCode.JOURNEY_INVALID,
            message: backendMessage,
            status,
            retriable: false
        };
    }
    if (status >= 500) {
        return {
            code: UploadErrorCode.PRESIGN_FAILED,
            message: "Server error while issuing the upload URL. Retrying…",
            status,
            retriable: true
        };
    }
    return {
        code: UploadErrorCode.PRESIGN_FAILED,
        message: backendMessage,
        status,
        retriable: false,
        backendCode
    };
}

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
